import { Link } from 'react-router-dom'
import { Badge, Button } from '../../components/ui'
import type { BookingSummary } from '../../types/storefront'
import { formatMoney } from '../../utils/format'
import { useTenant } from '../../tenant/useTenant'
import { bookingWhenShort, statusLabel, statusTone } from './bookingDisplay'

interface BookingCardProps {
  booking: BookingSummary
  /** False hides the cancel action entirely — the salon does not allow self-service. */
  cancelAllowed: boolean
  /** Past visits get the quieter, tighter treatment Stitch gives them. */
  past?: boolean
}

export function BookingCard({ booking, cancelAllowed, past = false }: BookingCardProps) {
  const { slug, bootstrap } = useTenant()
  const staff = booking.services.map((s) => s.staff_name).filter(Boolean)
  const title = booking.services[0]?.name ?? booking.code
  const price = formatMoney(booking.total, bootstrap.currency)

  if (past) {
    return (
      <div className="flex flex-col justify-between gap-4 rounded-xl border border-primary/5 bg-surface-container p-6 md:flex-row md:items-center">
        <div className="min-w-0">
          <h3 className="truncate font-body-lg text-body-lg font-bold text-on-surface">{title}</h3>
          <p className="mt-1 font-body-md text-body-md text-on-surface-variant">
            {[bookingWhenShort(booking), ...staff].filter(Boolean).join(' • ')}
          </p>
        </div>
        <div className="flex items-center gap-6 md:justify-end">
          <span className="font-body-md text-body-md text-on-surface-variant">{price}</span>
          <Link
            to={`/${slug}/account/bookings/${booking.id}`}
            className="font-label-caps text-label-caps uppercase text-primary transition-colors hover:text-primary-container"
          >
            Details
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="relative flex flex-col justify-between gap-6 overflow-hidden rounded-xl border border-primary/10 bg-surface p-8 md:flex-row md:items-center">
      {/* Stitch marks a live appointment with a full-height rule in the primary. */}
      <span aria-hidden="true" className="absolute left-0 top-0 h-full w-1 bg-primary" />

      <div className="min-w-0 flex-grow space-y-4">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-label-caps text-label-caps uppercase text-primary">
              {bookingWhenShort(booking)}
            </span>
            <Badge tone={statusTone(booking.status)}>{statusLabel(booking.status)}</Badge>
          </div>
          <h3 className="mt-1 truncate font-display text-headline-sm text-on-surface">{title}</h3>
          {booking.services.length > 1 && (
            <p className="mt-1 font-body-md text-body-md text-on-surface-variant">
              + {booking.services.length - 1} more service{booking.services.length > 2 ? 's' : ''}
            </p>
          )}
        </div>

        <dl className="flex flex-wrap gap-x-6 gap-y-2 font-body-md text-body-md text-on-surface-variant">
          {staff.length > 0 && (
            <div className="flex items-center gap-2">
              <span aria-hidden="true" className="material-symbols-outlined text-[18px]">
                person
              </span>
              <dt className="sr-only">Specialist</dt>
              <dd>{staff.join(', ')}</dd>
            </div>
          )}
          <div className="flex items-center gap-2">
            <span aria-hidden="true" className="material-symbols-outlined text-[18px]">
              location_on
            </span>
            <dt className="sr-only">Location</dt>
            <dd>{booking.branch.name}</dd>
          </div>
          <div className="flex items-center gap-2 font-bold text-primary">
            <dt className="sr-only">Total</dt>
            <dd>{price}</dd>
          </div>
        </dl>
      </div>

      <div className="flex w-full flex-shrink-0 flex-col gap-3 md:w-auto md:items-end">
        <Button to={`/${slug}/account/bookings/${booking.id}`} variant="outlined" size="md" fullWidth className="md:w-auto">
          View details
        </Button>
        {booking.can_reschedule && (
          <Link
            to={`/${slug}/account/bookings/${booking.id}/reschedule`}
            className="text-center font-label-caps text-label-caps uppercase text-primary transition-colors hover:text-primary-container"
          >
            Reschedule
          </Link>
        )}
        {cancelAllowed && booking.can_cancel && (
          <Link
            to={`/${slug}/account/bookings/${booking.id}/cancel`}
            className="text-center font-label-caps text-label-caps uppercase text-on-surface-variant transition-colors hover:text-error"
          >
            Cancel
          </Link>
        )}
      </div>
    </div>
  )
}
